import React, { useEffect, useState } from "react";
import {
    FaBook,
    FaPen,
    FaClock,
    FaArrowLeft,
    FaFilter,
    FaBookmark,
    FaRegBookmark,
    FaRandom,
    FaChartBar,
    FaSearch,
    FaQuestionCircle,
    FaTrophy,
    FaPlay,
    FaGraduationCap,
    FaFileAlt,
    FaBoxOpen,
    FaUsers,
    FaCalendarAlt,
    FaHourglassHalf,
    FaLock,
    FaCheckCircle,
    FaBookOpen,
    FaCalendarTimes,
    FaTimesCircle
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { useQuiz } from "../../utils/api/Redux/QuizContext";
import { ClipLoader } from "react-spinners";
import AnimatedNumber from "../../components/counterAnimation";
import toast, { Toaster } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

const SingleSubjectQuiz = () => {
    const [quizData, setLocalQuizData] = useState([]);
    const [filters, setFilters] = useState({});
    const [mode, setMode] = useState("");
    const [timeLeft, setTimeLeft] = useState(6300); // 1h 45m
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState("");
    const [selectedDifficulty, setSelectedDifficulty] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [userProgress, setUserProgress] = useState({});
    const { setQuizData } = useQuiz();
    const [incompleteTests, setIncompleteTests] = useState([]);
    const [allQuestions, setAllQuestions] = useState([]);
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    // Keep a single state declaration at the top
    const [subjectStats, setSubjectStats] = useState({
        total: 0,
        topics: {},
        difficulty: {}
    });

    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const user = jwtDecode(token)
    const user_id = user?.user_id;
    const params = new URLSearchParams(window.location.search);
    // const [subjectStats, setSubjectStats] = useState({ topics: {}, difficulty: {} });
    const subject = params.get("subject");

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, []); // runs only on mount


    useEffect(() => {
        const fetchUpcomingExams = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/tests/upcoming/${subject}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!res.ok) {
                    const data = await res.json();
                    console.log(data)
                    throw new Error(data.message || "Failed to fetch upcoming exams");
                }

                const data = await res.json();
                console.log(data)
                setExams(data?.exams);
            } catch (err) {
                console.error("Error fetching upcoming exams:", err.message);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUpcomingExams();
    }, [token]);

    useEffect(() => {
        const fetchBookmarks = async () => {
            try {
                const token = localStorage.getItem("token"); // or wherever you store it
                const res = await fetch(`${API_BASE_URL}/api/bookmarks/${subject}`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`, // add token here
                    },
                });

                if (!res.ok) throw new Error("Failed to fetch bookmarks");

                const data = await res.json();
                setBookmarkedQuestions(new Set(data));
            } catch (err) {
                console.error(err.message);
            }
        };

        fetchBookmarks();
    }, [subject, user_id]);


    // Load user progress from localStorage
    useEffect(() => {
        const savedProgress = localStorage.getItem(`progress_${subject}_${user_id}`);
        if (savedProgress) {
            setUserProgress(JSON.parse(savedProgress));
        }
    }, [subject, user_id]);

    const Countdown = ({ targetTime, color }) => {
        const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetTime));

        useEffect(() => {
            const timer = setInterval(() => setTimeLeft(getTimeLeft(targetTime)), 1000);
            return () => clearInterval(timer);
        }, [targetTime]);

        if (!timeLeft) return <span className={`text-${color}-700 text-sm font-medium`}>0h 0m 0s</span>;

        const { hours, minutes, seconds } = timeLeft;

        return (
            <div className={`flex items-center gap-1 text-${color}-700`}>
                <div className={`bg-${color}-100 px-2 py-1 rounded text-xs font-mono font-bold`}>
                    {hours.toString().padStart(2, '0')}h
                </div>
                <div className={`bg-${color}-100 px-2 py-1 rounded text-xs font-mono font-bold`}>
                    {minutes.toString().padStart(2, '0')}m
                </div>
                <div className={`bg-${color}-100 px-2 py-1 rounded text-xs font-mono font-bold`}>
                    {seconds.toString().padStart(2, '0')}s
                </div>
            </div>
        );
    };

    const getTimeLeft = (targetTime) => {
        const diff = targetTime - new Date().getTime();
        if (diff <= 0) return null;
        return {
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
        };
    };


    // Fetch questions
    useEffect(() => {
        const fetchQuestions = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/quiz/subjects/${subject}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch questions");
                }

                const data = await response.json();
                console.log(data)
                setAllQuestions(data?.questions)
                setLocalQuizData(data?.questions);
            } catch (error) {
                console.error("Error fetching questions:", error.message);
                toast.error(error.message)
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, [subject]);

    // Fetch incomplete tests
    useEffect(() => {
        const fetchIncompleteTests = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/tests/continue`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch incomplete tests");
                }

                const incomplete = await response.json();
                setIncompleteTests(incomplete.data); // set all incomplete tests
            } catch (error) {
                console.error("Error fetching incomplete tests:", error.message);
            }
        };

        fetchIncompleteTests();
    }, [user_id, subject]);

    // Calculate stats once both localQuizData and incompleteTests are ready
    useEffect(() => {
        if (allQuestions?.length > 0) {
            calculateSubjectStats(incompleteTests, allQuestions);
        }

    }, [allQuestions, incompleteTests]);


    const calculateSubjectStats = (incompleteTests, questions) => {
        const topicStats = {};
        const difficultyStats = { Easy: 0, Medium: 0, Hard: 0 };

        // Flatten all answers
        const allAnswers = incompleteTests.flatMap(test => test.answers);

        // Build answered lookup
        const answeredLookup = {};
        allAnswers.forEach(ans => {
            answeredLookup[ans.question_id] = {
                selected_option: ans.selected_option,
                correct: ans.is_correct === 1
            };
        });

        // Loop over all questions for this subject to get total counts
        questions.forEach(q => {
            // Initialize topic
            if (!topicStats[q.topic]) {
                topicStats[q.topic] = { total: 0, completed: 0, correct: 0, incorrect: 0 };
            }

            topicStats[q.topic].total++;

            // Check if answered
            const progress = answeredLookup[q.id];
            if (progress) {
                topicStats[q.topic].completed++;
                if (progress.correct) topicStats[q.topic].correct++;
                else topicStats[q.topic].incorrect++;
            }

            // Difficulty
            if (difficultyStats.hasOwnProperty(q.level)) {
                difficultyStats[q.level]++;
            }
        });

        setSubjectStats({
            total: questions?.length,
            topics: topicStats,
            difficulty: difficultyStats
        });
    };


    const continueQuiz = async (test, quizMode) => {
        if (!test) return;
        // Preserve the test's original mode so resumed answers are saved with the
        // same mode they started with. Without this, the teacher's analysis
        // endpoint (which filters mode = "exam") drops every answer the student
        // gives in the resumed session.
        const resolvedMode = quizMode || test.answers?.[0]?.mode || "exam";
        console.log(test)

        // Already answered questions (keep order as is)
        const answeredQuestions = test.answers.map(ans => ({
            ...ans.quiz,
            selectedOption: ans.selected_option,
        }));

        // Remaining questions from the question bank that haven't been answered
        const allQuestionBank = getFilteredQuestions();
        let unansweredQuestions = allQuestionBank.filter(
            (q) => !answeredQuestions.some(a => a.id === q.id)
        );

        // Apply mode-specific slicing/shuffling only on unanswered questions
        if (resolvedMode === "exam") {
            unansweredQuestions = unansweredQuestions.sort(() => Math.random() - 0.5).slice(0, 40 - answeredQuestions?.length);
        } else if (resolvedMode === "study") {
            if (selectedTopic) {
                unansweredQuestions = unansweredQuestions.slice(0, 20 - answeredQuestions?.length);
            } else {
                unansweredQuestions = unansweredQuestions.sort(() => Math.random() - 0.5).slice(0, 20 - answeredQuestions?.length);
            }
        } else if (resolvedMode === "random") {
            unansweredQuestions = unansweredQuestions.sort(() => Math.random() - 0.5).slice(0, 10 - answeredQuestions?.length);
        }

        // Merge answered first, then unanswered
        const selectedQuestions = [...answeredQuestions, ...unansweredQuestions];

        // Set quiz data
        setQuizData(selectedQuestions);

        // Navigate to exam page with previous answers prefilled
        navigate("/exam", {
            state: {
                test_id: test.test_id,
                mode: resolvedMode,
                subject: subject,
                previousAnswers: answeredQuestions.map(ans => ({
                    question_id: ans.id,
                    selected_option: ans.selected_option,
                })),
            },
        });
    };




    const toggleBookmark = async (topic) => {
        const token = localStorage.getItem("token"); // or wherever your token is stored
        const isBookmarked = bookmarkedQuestions.has(topic);

        if (isBookmarked) {
            await fetch(`${API_BASE_URL}/api/bookmarks`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ subject, topic_or_question_id: topic }),
            });

            setBookmarkedQuestions(prev => {
                const updated = new Set(prev);
                updated.delete(topic);
                return updated;
            });
        } else {
            await fetch(`${API_BASE_URL}/api/bookmarks`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ subject, topic_or_question_id: topic }),
            });

            setBookmarkedQuestions(prev => new Set(prev).add(topic));
        }
    };


    const getFilteredQuestions = () => {
        let filtered = [...quizData];

        // Topic filter
        if (selectedTopic) {
            filtered = filtered.filter(q => q.topic === selectedTopic);
        }

        // Difficulty filter
        if (selectedDifficulty) {
            filtered = filtered.filter(q => q.level === selectedDifficulty);
        }

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(q =>
                q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.topic.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    };

    const startQuiz = async (quizMode = mode, customQuestions = null) => {
        if (!quizMode) return;

        let selectedQuestions = customQuestions || getFilteredQuestions();

        // Apply mode-specific logic
        if (quizMode === "exam") {
            selectedQuestions = selectedQuestions.sort(() => Math.random() - 0.5).slice(0, 40);
        } else if (quizMode === "study") {
            if (selectedTopic) {
                selectedQuestions = selectedQuestions.slice(0, 20);
            } else {
                selectedQuestions = selectedQuestions.sort(() => Math.random() - 0.5).slice(0, 20);
            }
        } else if (quizMode === "random") {
            selectedQuestions = selectedQuestions.sort(() => Math.random() - 0.5).slice(0, 10);
        }

        setQuizData(selectedQuestions);
        const testId = await createTest();

        if (testId) {
            navigate("/exam", {
                state: {
                    test_id: testId,
                    time_left: timeLeft,
                    mode: quizMode,
                    subject: subject
                }
            });
        }
    };

    const startTopicStudy = (topic) => {
        const topicQuestions = quizData.filter(q => q.topic === topic);
        startQuiz("study", topicQuestions);
    };

    const startBookmarkedQuestions = () => {
        const bookmarked = quizData.filter(q => bookmarkedQuestions.has(q.id));
        if (bookmarked?.length === 0) {
            alert("No bookmarked questions found!");
            return;
        }
        startQuiz("study", bookmarked);
    };

    const createTest = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tests`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create test");
            }

            const test = await response.json();
            localStorage.setItem('test_id', test.data.id);
            return test.data.id;
        } catch (error) {
            console.error("Error creating test:", error.message);
            return null;
        }
    };

    const goBack = () => {
        navigate(-1);
    };

    // Get unique values for filters
    const uniqueTopics = [...new Set(quizData?.map(q => q.topic))];
    const uniqueLevels = [...new Set(quizData?.map(q => q.level))];

    const [timeLefts, setTimeLefts] = useState({}); // store countdown per exam

    useEffect(() => {
        const timers = setInterval(() => {
            setTimeLefts(prev => {
                const newTimes = {};
                exams.forEach(exam => {
                    const start = new Date(exam.startTime);
                    const now = new Date();
                    newTimes[exam.exam_id] = Math.max(0, Math.floor((start - now) / 1000));
                });
                return newTimes;
            });
        }, 1000);

        return () => clearInterval(timers);
    }, [exams]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <ClipLoader color="#4A90E2" size={50} />
                <p className="ml-4 text-blue-600 font-medium">Loading {subject} questions...</p>
            </div>
        );
    }


    const startExam = async (exam) => {
        console.log(exam);
        try {
            if (exam.started) {
                toast.error("You have already taken this exam.");
                return;
            }

            const res = await fetch(`${API_BASE_URL}/api/quiz/${exam.exam_id}/questions`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!res.ok) throw new Error("Failed to fetch exam questions");
            let selectedQuestions = await res.json();

            // 🔀 Shuffle questions randomly
            selectedQuestions = selectedQuestions
                .map(q => ({ ...q, sort: Math.random() }))
                .sort((a, b) => a.sort - b.sort)
                .map(q => {
                    delete q.sort;
                    return q;
                });

            setQuizData(selectedQuestions);
            console.log(exam.tests?.[0]?.id)
            const testId = exam.tests?.[0]?.id;
            if (!testId) throw new Error("Failed to create a new test");

            const now = new Date();
            const startTime = new Date(exam.startTime);

            // Duration (user’s exam duration in seconds)
            const durationSeconds = exam.duration * 60;

            // Available duration (exam window in hours → seconds)
            const availableWindowEnd = new Date(
                startTime.getTime() + exam.availableDuration * 60 * 60 * 1000
            );

            // ✅ Remaining available duration for the exam (for everyone)
            const remainingAvailableSeconds = Math.max(
                Math.floor((availableWindowEnd - now) / 1000),
                0
            );

            if (remainingAvailableSeconds <= 0) {
                toast.error("This exam is no longer available.");
                return;
            }

            // ✅ Compute user's exam duration as before
            const userEndTime = now.getTime() + durationSeconds * 1000;
            const finalEndTime = Math.min(userEndTime, availableWindowEnd.getTime());
            const remainingUserTime = Math.floor((finalEndTime - now.getTime()) / 1000);

            console.log({
                testId,
                remainingUserTime,
                remainingAvailableSeconds,
                subject: exam.subject,
            });

            // ✅ Pass BOTH remaining times to the next page
            navigate("/exam", {
                state: {
                    test_id: testId,
                    subject: exam.subject,
                    mode: "exam",
                    time_left: remainingUserTime, // user's personal timer
                    available_time_left: remainingAvailableSeconds, // total window remaining
                },
            });
        } catch (err) {
            console.error("Error starting exam:", err);
            alert("Could not start exam. Please try again later.");
        }
    };



    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with Back Button */}
            <div className="bg-white shadow-sm">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition-colors"
                    >
                        <FaArrowLeft className="text-lg" />
                        Back to Subjects
                    </button>
                </div>
            </div>
            <Toaster position="top-right" reverseOrder={false} />
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Subject Header */}
                {/* <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-[#2148C0] mb-3">
                        {subject} Practice
                    </h1>
                    <div className="flex justify-center items-center gap-6 text-gray-600">
                        <span className="flex items-center gap-1">
                            Total Questions: <AnimatedNumber value={subjectStats.total || 0} />
                        </span>
                        <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                        <span className="flex items-center gap-1">
                            Topics: <AnimatedNumber value={Object.keys(subjectStats?.topics || {})?.length} />
                        </span>
                    </div>
                </div> */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-[#2148C0] mb-3">
                        {subject} Practice
                    </h1>
                    <div className="flex justify-center items-center gap-6 text-gray-600">
                        <span className="flex items-center gap-1">
                            Topics: <AnimatedNumber value={Object.keys(subjectStats?.topics || {})?.length} />
                        </span>
                    </div>
                </div>


                {/* Quick Stats Cards */}
                <div className="grid grid-cols-3 lg:grid-cols-3 gap-4 mb-8">
                    {/* 
  <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100">
    <div className="flex items-center justify-between mb-3">
      <FaBook className="text-2xl text-blue-600" />
      <span className="text-2xl font-bold text-blue-600">
        <AnimatedNumber value={subjectStats.total || 0} />
      </span>
         </div>
           <h3 className="font-semibold text-gray-700">Total Questions</h3>
          </div>
  */}

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
                        <div className="flex items-center justify-between mb-3">
                            <FaTrophy className="text-2xl text-green-600" />
                            <span className="text-2xl font-bold text-green-600">
                                {Object.values(subjectStats?.topics || {}).reduce(
                                    (total, topic) => total + (topic.completed || 0),
                                    0
                                )}
                            </span>
                        </div>
                        <h3 className="font-semibold text-gray-700">Completed</h3>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-yellow-100">
                        <div className="flex items-center justify-between mb-3">
                            <FaBookmark className="text-2xl text-yellow-600" />
                            <span className="text-2xl font-bold text-yellow-600">
                                <AnimatedNumber value={bookmarkedQuestions.size} />
                            </span>
                        </div>
                        <h3 className="font-semibold text-gray-700">Bookmarked</h3>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-100">
                        <div className="flex items-center justify-between mb-3">
                            <FaQuestionCircle className="text-2xl text-purple-600" />
                            <span className="text-2xl font-bold text-purple-600">
                                <AnimatedNumber value={uniqueTopics?.length} />
                            </span>
                        </div>
                        <h3 className="font-semibold text-gray-700">Topics</h3>
                    </div>
                </div>


                {/* Upcoming Exams Section */}
                {exams?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-indigo-100 p-3 rounded-lg">
                                <FaGraduationCap className="text-indigo-600 text-2xl" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900">Upcoming Exams</h2>
                                <p className="text-gray-600 text-sm mt-1">{exams?.length} exam{exams?.length !== 1 ? 's' : ''} scheduled</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {exams.map((exam) => {
                                const timeLeft = timeLefts[exam.exam_id] || 0;
                                const hours = Math.floor(timeLeft / 3600);
                                const minutes = Math.floor((timeLeft % 3600) / 60);
                                const seconds = timeLeft % 60;
                                const isUpcoming = timeLeft > 0;
                                const progress = exam.tests?.[0]?.progress || 0;

                                return (
                                    <div
                                        key={exam.exam_id}
                                        className="bg-gradient-to-br from-white to-indigo-50 p-6 rounded-xl border-2 border-indigo-100 hover:border-indigo-200 transition-all duration-300 hover:shadow-lg group"
                                    >
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FaBookOpen className="text-indigo-600 text-lg" />
                                                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{exam.title}</h3>
                                                </div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaBook className="text-indigo-500 text-sm" />
                                                    <p className="text-indigo-700 font-semibold text-sm">{exam.subject}</p>
                                                </div>
                                            </div>
                                            <div className="bg-indigo-100 p-2 rounded-lg">
                                                <FaFileAlt className="text-indigo-600 text-lg" />
                                            </div>
                                        </div>

                                        {/* Details */}
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                <FaUsers className="text-gray-500" />
                                                <span>Group: {exam.Group?.groupName}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                <FaCalendarAlt className="text-gray-500" />
                                                <span>
                                                    {new Date(exam.startTime).toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                <FaClock className="text-gray-500" />
                                                <span>
                                                    {new Date(exam.startTime).toLocaleTimeString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit"
                                                    })}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Timer/Status */}
                                        <div className="mb-4">
                                            {exam.started ? (
                                                // ✅ Already taken
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="bg-red-100 p-1 rounded-full">
                                                            <FaTimesCircle className="text-red-600 text-sm" />
                                                        </div>
                                                        <span className="text-red-800 font-semibold text-sm">
                                                            You have already taken this exam
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (() => {
                                                // 🕒 Determine status
                                                const now = new Date().getTime();
                                                const start = new Date(exam.startTime).getTime();
                                                const end = start + (exam.availableDuration || 0) * 60 * 60 * 1000;

                                                if (now < start) {
                                                    // Exam not started yet ⏳
                                                    return (
                                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <FaHourglassHalf className="text-amber-600 animate-pulse" />
                                                                <span className="text-amber-800 font-semibold text-sm">Starts in:</span>
                                                            </div>
                                                            <Countdown targetTime={start} color="amber" />
                                                        </div>
                                                    );
                                                } else if (now >= start && now < end) {
                                                    // Exam open ✅
                                                    return (
                                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <FaCheckCircle className="text-green-600" />
                                                                <span className="text-green-800 font-semibold text-sm">Open for:</span>
                                                            </div>
                                                            <Countdown targetTime={end} color="green" />
                                                        </div>
                                                    );
                                                } else {
                                                    // Exam window passed ⛔
                                                    return (
                                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                                            <div className="flex items-center gap-2">
                                                                <FaLock className="text-gray-500" />
                                                                <span className="text-gray-700 font-semibold text-sm">Exam closed</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>


                                        {/* Progress Bar */}
                                        {progress > 0 && (
                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-600">Progress</span>
                                                    <span className="text-xs font-bold text-indigo-600">{progress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${progress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}



                                        {/* Action Button */}
                                        <button
                                            disabled={isUpcoming || exam.started}
                                            onClick={() => startExam(exam)}
                                            className={`w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${isUpcoming || exam.started
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 shadow-md hover:shadow-lg group-hover:shadow-xl transform hover:-translate-y-0.5'
                                                }`}
                                        >
                                            {exam.started ? (
                                                <>
                                                    <FaTimesCircle className="text-sm" />
                                                    <span>Already Taken</span>
                                                </>
                                            ) : isUpcoming ? (
                                                <>
                                                    <FaLock className="text-sm" />
                                                    <span>Waiting to Start</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FaPlay className="text-sm" />
                                                    <span>Start Exam</span>
                                                </>
                                            )}
                                        </button>

                                    </div>
                                );
                            })}
                        </div>

                        {/* Empty State (if needed) */}
                        {exams?.length === 0 && (
                            <div className="text-center py-12">
                                <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FaCalendarTimes className="text-gray-400 text-2xl" />
                                </div>
                                <h3 className="text-gray-600 font-semibold text-lg mb-2">No Upcoming Exams</h3>
                                <p className="text-gray-500 text-sm">You're all caught up! New exams will appear here when scheduled.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4 mb-4">
                        <div className="flex-1 relative">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search questions or topics..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${showAdvancedFilters
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <FaFilter />
                                Filters
                            </button>
                            <button
                                onClick={() => setShowAnalytics(!showAnalytics)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${showAnalytics
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <FaChartBar />
                                Analytics
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters Panel */}
                    {showAdvancedFilters && (
                        <div className="bg-gray-50 rounded-lg p-6 border">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block font-semibold text-gray-700 mb-2">Filter by Topic</label>
                                    <select
                                        value={selectedTopic}
                                        onChange={(e) => setSelectedTopic(e.target.value)}
                                        className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Topics</option>
                                        {uniqueTopics.map(topic => (
                                            <option key={topic} value={topic}>{topic}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-semibold text-gray-700 mb-2">Filter by Difficulty</label>
                                    <select
                                        value={selectedDifficulty}
                                        onChange={(e) => setSelectedDifficulty(e.target.value)}
                                        className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Difficulty Levels</option>
                                        {uniqueLevels.map(level => (
                                            <option key={level} value={level}>{level}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setSelectedTopic("");
                                    setSelectedDifficulty("");
                                    setSearchTerm("");
                                }}
                                className="mt-4 text-red-600 hover:text-red-700 font-medium transition-colors"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}
                </div>

                {/* Analytics Panel */}
                {showAnalytics && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-6">Performance Analytics</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Topic Progress */}
                            <div>
                                <h4 className="font-semibold text-gray-700 mb-4">Progress by Topic</h4>
                                <div className="space-y-4">
                                    {Object.entries(subjectStats?.topics || {}).map(([topic, stats]) => {
                                        const percentage = stats.total > 0 ? ((stats.completed / stats.total) * 100) : 0;
                                        const roundedPercent = Math.round(percentage);
                                        return (
                                            <div key={topic} className="bg-gray-50 p-4 rounded-lg">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium text-gray-800">{topic}</span>
                                                    <span className="text-sm text-gray-600">
                                                        {stats.completed}/{stats.total} (~{roundedPercent}%)
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-3">
                                                    <div
                                                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-700"
                                                        style={{ width: `${roundedPercent}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Difficulty Distribution */}
                            <div>
                                <h4 className="font-semibold text-gray-700 mb-4">Question Distribution by Difficulty</h4>
                                <div className="space-y-3">
                                    {Object.entries(subjectStats.difficulty || {}).map(([level, count]) => {
                                        const approxCount = Math.max(1, Math.round(count));
                                        return (
                                            <div key={level} className="bg-gray-50 p-4 rounded-lg">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-gray-800">{level}</span>
                                                    <span className="text-blue-600 font-semibold">~{approxCount} questions</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Practice Modes */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
                                <FaPen className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold">Exam Mock</h3>
                        </div>
                        <p className="mb-6 text-blue-100 leading-relaxed">
                            Full exam simulation with 40 random questions under timed conditions.
                        </p>
                        <button
                            onClick={() => startQuiz("exam")}
                            disabled={quizData?.length === 0}
                            className="w-full bg-white text-blue-600 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Start Exam Mock
                        </button>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
                                <FaBook className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold">Study Mode</h3>
                        </div>
                        <p className="mb-6 text-green-100 leading-relaxed">
                            Practice with filtered questions, see explanations, and track progress.
                        </p>
                        <button
                            onClick={() => startQuiz("study")}
                            disabled={quizData?.length === 0}
                            className="w-full bg-white text-green-600 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Start Studying
                        </button>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
                                <FaRandom className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold">Random Practice</h3>
                        </div>
                        <p className="mb-6 text-purple-100 leading-relaxed">
                            Quick practice with 10 random questions for variety and revision.
                        </p>
                        <button
                            onClick={() => startQuiz("random")}
                            disabled={quizData?.length === 0}
                            className="w-full bg-white text-purple-600 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Random Practice
                        </button>
                    </div>
                </div>

                {/* Timer Settings for Exam Mode */}
                {mode === "exam" && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                        <div className="flex items-center mb-4">
                            <FaClock className="text-blue-600 text-2xl mr-3" />
                            <h3 className="text-xl font-semibold text-gray-800">Exam Timer Settings</h3>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-3">
                                <label className="font-medium text-gray-700">Hours:</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="3"
                                    value={Math.floor(timeLeft / 3600)}
                                    onChange={(e) => {
                                        const hours = parseInt(e.target.value, 10) || 0;
                                        setTimeLeft(hours * 3600 + (timeLeft % 3600));
                                    }}
                                    className="w-20 p-3 border border-gray-200 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="font-medium text-gray-700">Minutes:</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={Math.floor((timeLeft % 3600) / 60)}
                                    onChange={(e) => {
                                        const minutes = parseInt(e.target.value, 10) || 0;
                                        setTimeLeft(Math.floor(timeLeft / 3600) * 3600 + minutes * 60);
                                    }}
                                    className="w-20 p-3 border border-gray-200 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Study by Topic */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Study by Topic</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Object.entries(subjectStats?.topics || {}).map(([topic, stats]) => {
                            const percentage = stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(1) : 0;
                            return (
                                <div key={topic} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="font-semibold text-gray-800 text-lg">{topic}</h3>
                                        <button
                                            onClick={() => toggleBookmark(topic)}
                                            className={`transition-colors ${bookmarkedQuestions.has(topic)
                                                ? 'text-yellow-500 hover:text-yellow-600'
                                                : 'text-gray-400 hover:text-yellow-500'
                                                }`}
                                        >
                                            {bookmarkedQuestions.has(topic) ? <FaBookmark /> : <FaRegBookmark />}
                                        </button>
                                    </div>

                                    <div className="mb-4">
                                        {/* <div className="flex justify-between text-sm text-gray-600 mb-2">
                                            <span>{stats.total} questions</span>
                                            <span>{stats.completed} completed ({percentage}%)</span>
                                        </div> */}
                                        {/* <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div> */}
                                    </div>

                                    <button
                                        onClick={() => startTopicStudy(topic)}
                                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                                    >
                                        <FaPlay className="text-sm" />
                                        Study This Topic
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <button
                        onClick={startBookmarkedQuestions}
                        disabled={bookmarkedQuestions.size === 0}
                        className="bg-white border-2 border-yellow-200 text-yellow-700 py-4 px-6 rounded-xl hover:bg-yellow-50 hover:border-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-medium"
                    >
                        <FaBookmark className="text-xl" />
                        Practice Bookmarked Questions ({bookmarkedQuestions.size})
                    </button>
                    <button
                        onClick={() => {
                            const filtered = getFilteredQuestions();
                            startQuiz("study", filtered);
                        }}
                        disabled={getFilteredQuestions()?.length === 0}
                        className="bg-white border-2 border-green-200 text-green-700 py-4 px-6 rounded-xl hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-medium"
                    >
                        <FaFilter className="text-xl" />
                        Practice Filtered Questions ({getFilteredQuestions()?.length})
                    </button>
                </div>

                {/* Incomplete Tests */}
                {incompleteTests?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                            <FaClock className="text-orange-500" />
                            Continue Where You Left Off
                        </h2>
                        <div className="space-y-4">
                            {incompleteTests.map((test, index) => {
                                const totalQuestions = 20;
                                const answered = test.answers?.length;
                                const progress = Math.min((answered / totalQuestions) * 100, 100);

                                return (
                                    <div
                                        key={test.test_id}
                                        className="flex flex-col lg:flex-row items-start lg:items-center justify-between bg-orange-50 p-6 rounded-lg border border-orange-200"
                                    >
                                        <div className="flex-1 w-full lg:mr-6">
                                            <h3 className="font-semibold text-gray-800 mb-2">
                                                Incomplete Test #{index + 1}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {answered} of {totalQuestions} questions answered
                                            </p>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div
                                                    className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full transition-all duration-500"
                                                    style={{ width: `${progress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => continueQuiz(test)}
                                            className="mt-4 lg:mt-0 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center gap-2"
                                        >
                                            <FaPlay className="text-sm" />
                                            Continue Test
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <footer className="bg-[#2148C0] text-white py-6 mt-12">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <p className="text-blue-100">
                        &copy; {new Date().getFullYear()} TeeChaa CBT Application. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default SingleSubjectQuiz;